import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { type ESize, InputBorderEnum } from '@common/enums';
import { ERadioGroupType } from '@ui/sp-radio-group';
import type { ProductVariantsSettingsFormGroup } from '@web/widgets/product-card/type';
import { type ProductVariantsSettingsFormType } from '@web/widgets/product-card/type';
import { ThemeStateService } from '@sites/site-theme/src/state';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import { CSS_RESTRICTIONS } from '@common/constants';
import { TranslateModule } from '@ngx-translate/core';
import { SpRadioGroupComponent } from '@ui/sp-radio-group';
import { SpInputNumComponent } from '@components/sp-form-controls/sp-input-num/sp-input-num.component';
import { SpColorPickerModule } from '@components/sp-color-picker/src/sp-color-picker.module';
import { CommonModule } from '@angular/common';
import { SpSwitchComponent } from '@ui/sp-switch';

@Component({
    selector: 'web-product-variants-settings',
    templateUrl: './product-variants-settings.component.html',
    styleUrls: ['./product-variants-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SpSwitchComponent,
        CommonModule,
        SpColorPickerModule,
        SpInputNumComponent,
        SpRadioGroupComponent,
        TranslateModule,
    ],
})
export class ProductVariantsSettingsComponent {
    private readonly themeState = inject(ThemeStateService);

    @Input() productVariantsSettingsFormGroup: ProductVariantsSettingsFormGroup;

    protected readonly InputBorderEnum = InputBorderEnum;
    protected readonly ERadioGroupType = ERadioGroupType;

    public readonly selectedThemeColors = this.themeState.selectedColors;

    public setIsShowFieldsName(val: boolean): void {
        this.productVariantsSettingsFormGroup.markAsTouched({ onlySelf: true });

        this.showLabels.patchValue(val);
    }

    public setTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.productVariantsSettingsFormGroup.markAsTouched({ onlySelf: true });

        this.textColor.patchValue(presetName ?? value);
    }

    public setInputBorderType(borderType: InputBorderEnum): void {
        this.productVariantsSettingsFormGroup.markAsTouched({ onlySelf: true });

        if (borderType === this.InputBorderEnum.BOTTOM_BORDER) {
            this.borderRadius.patchValue(0, { emitEvent: false });
        }

        this.inputBorderType.patchValue(borderType);
    }

    public setInputTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.productVariantsSettingsFormGroup.markAsTouched({ onlySelf: true });

        this.inputTextColor.patchValue(presetName ?? value);
    }

    public setThemeBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.productVariantsSettingsFormGroup.markAsTouched({ onlySelf: true });

        this.backgroundColor.patchValue(presetName ?? value);
    }

    public setBorderRadius(value: number): void {
        this.productVariantsSettingsFormGroup.markAsTouched({ onlySelf: true });

        this.borderRadius.patchValue(value);
    }

    public setBorderColor({ value, presetName }: ColorPickerEventPayload): void {
        this.productVariantsSettingsFormGroup.markAsTouched({ onlySelf: true });

        this.borderColor.patchValue(presetName ?? value);
    }

    public setInputSize(val: string): void {
        this.productVariantsSettingsFormGroup.markAsTouched({ onlySelf: true });

        this.inputSize.patchValue(val as ESize);
    }

    public get isDisabled(): boolean {
        return this.inputBorderType.value === this.InputBorderEnum.BOTTOM_BORDER;
    }

    public get BORDER_RADIUS_MIN(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    }

    public get BORDER_RADIUS_MAX(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MAX;
    }

    public get inputSize(): ProductVariantsSettingsFormType['inputSize'] {
        return this.productVariantsSettingsFormGroup.get('inputSize') as ProductVariantsSettingsFormType['inputSize'];
    }

    public get showLabels(): ProductVariantsSettingsFormType['showLabels'] {
        return this.productVariantsSettingsFormGroup.get('showLabels') as ProductVariantsSettingsFormType['showLabels'];
    }

    public get textColor(): ProductVariantsSettingsFormType['textColor'] {
        return this.productVariantsSettingsFormGroup.get('textColor') as ProductVariantsSettingsFormType['textColor'];
    }

    public get inputBorderType(): ProductVariantsSettingsFormType['inputBorderType'] {
        return this.productVariantsSettingsFormGroup.get(
            'inputBorderType'
        ) as ProductVariantsSettingsFormType['inputBorderType'];
    }

    public get inputTextColor(): ProductVariantsSettingsFormType['inputTextColor'] {
        return this.productVariantsSettingsFormGroup.get(
            'inputTextColor'
        ) as ProductVariantsSettingsFormType['inputTextColor'];
    }

    public get backgroundColor(): ProductVariantsSettingsFormType['backgroundColor'] {
        return this.productVariantsSettingsFormGroup.get(
            'backgroundColor'
        ) as ProductVariantsSettingsFormType['backgroundColor'];
    }

    public get borderColor(): ProductVariantsSettingsFormType['borderColor'] {
        return this.productVariantsSettingsFormGroup.get(
            'borderColor'
        ) as ProductVariantsSettingsFormType['borderColor'];
    }

    public get borderRadius(): ProductVariantsSettingsFormType['borderRadius'] {
        return this.productVariantsSettingsFormGroup.get(
            'borderRadius'
        ) as ProductVariantsSettingsFormType['borderRadius'];
    }
}
